import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { concatLatestFrom } from '@ngrx/operators';
import { catchError, map, of, switchMap, tap } from 'rxjs';

import {
  CartLine,
  CheckoutItem,
  CheckoutProduct,
  Order,
  ORDER_STEP_LABELS,
} from '../../core/models';
import { OrdersService } from '../../core/services/orders.service';
import { ToastService } from '../../core/services/toast.service';
import { selectStoreId } from '../auth/auth.selectors';
import { CatalogActions } from '../catalog/catalog.actions';
import { selectCartLines } from '../cart/cart.selectors';
import { ProfileActions } from '../profile/profile.actions';
import { StockActions } from '../stock/stock.actions';
import { OrdersActions } from './orders.actions';

export const loadOrders$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store), orders = inject(OrdersService)) =>
    actions$.pipe(
      ofType(OrdersActions.loadOrders),
      concatLatestFrom(() => store.select(selectStoreId)),
      switchMap(([{ startDate, endDate }, storeId]) => {
        if (!storeId) {
          return of(OrdersActions.loadOrdersFailure({ error: 'Missing store id.' }));
        }
        return orders.getOrders(startDate, endDate, storeId).pipe(
          map((list) => OrdersActions.loadOrdersSuccess({ orders: list })),
          catchError((err) =>
            of(OrdersActions.loadOrdersFailure({ error: err?.message ?? 'Load failed' })),
          ),
        );
      }),
    ),
  { functional: true },
);

function outOfStockMessage(
  outOfStock: Array<{ productId: string; availableQuantity: number }>,
  lines: CartLine[],
): string {
  const parts = outOfStock.map(({ productId, availableQuantity }) => {
    const name = lines.find((l) => l.product.id === productId)?.product.name ?? productId;
    return name + ' (only ' + availableQuantity + ' available)';
  });
  return (
    (outOfStock.length > 1 ? 'These products are' : 'This product is') +
    ' out of stock: ' +
    parts.join(', ')
  );
}

function todayLabel(): string {
  return new Date().toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
}

/**
 * Placing an order is a two-step check: validate stock + credit against the
 * warehouse first, then only checkout if every line is in stock and there's
 * credit left to spend.
 */
export const placeOrder$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store), orders = inject(OrdersService)) =>
    actions$.pipe(
      ofType(OrdersActions.placeOrder),
      concatLatestFrom(() => [store.select(selectCartLines), store.select(selectStoreId)]),
      switchMap(([, lines, storeId]) => {
        if (!lines.length) {
          return of(OrdersActions.placeOrderFailure({ error: 'Your basket is empty.' }));
        }
        if (!storeId) {
          return of(OrdersActions.placeOrderFailure({ error: 'Missing store id.' }));
        }

        const products: CheckoutProduct[] = lines.map((l) => ({
          _id: l.product.id,
          quantity: l.qty,
        }));
        const items: CheckoutItem[] = lines.map((l) => ({
          name: l.product.name,
          id: l.product.id,
          quantity: l.qty,
        }));

        return orders.checkStockAndCredit(storeId, products).pipe(
          switchMap((check) => {
            const outOfStock = check.products.filter((p) => !p.isInStock);
            if (outOfStock.length > 0) {
              return of(
                OrdersActions.placeOrderFailure({ error: outOfStockMessage(outOfStock, lines) }),
              );
            }
            if (check.isFranchise && check.availableCredit <= 0) {
              return of(
                OrdersActions.placeOrderFailure({
                  error: 'No credit available — settle an invoice before raising this order.',
                }),
              );
            }
            if (!check.success) {
              return of(
                OrdersActions.placeOrderFailure({
                  error: check.message || 'Unable to place this order.',
                }),
              );
            }

            return orders.checkout(storeId, items).pipe(
              map((res) => {
                if (res?.success === false) {
                  throw new Error(res.message || 'Checkout failed');
                }
                const order: Order = {
                  id: res?.orderId ?? 'TE-' + Date.now().toString().slice(-6),
                  date: todayLabel(),
                  units: lines.reduce((a, l) => a + l.qty, 0),
                  total: check.totalCost,
                  status: 'created',
                  statusLabel: ORDER_STEP_LABELS['created'],
                  customerName: '',
                  city: '',
                  items: lines.map((l) => ({
                    name: l.product.name,
                    quantity: l.qty,
                    price: l.product.price ?? 0,
                  })),
                  statusTimeline: { created: { date: new Date().toISOString() } },
                };
                return OrdersActions.placeOrderSuccess({ order });
              }),
              catchError((err) =>
                of(
                  OrdersActions.placeOrderFailure({
                    error: err?.error?.message ?? err?.message ?? 'Checkout failed',
                  }),
                ),
              ),
            );
          }),
          catchError((err) =>
            of(
              OrdersActions.placeOrderFailure({
                error: err?.error?.message ?? err?.message ?? 'Stock/credit check failed',
              }),
            ),
          ),
        );
      }),
    ),
  { functional: true },
);

/** On success: confirm & route to the success screen (cart + credit react
 *  in their own reducers to the same action). */
export const onPlaceOrderSuccess$ = createEffect(
  (actions$ = inject(Actions), router = inject(Router), toast = inject(ToastService)) =>
    actions$.pipe(
      ofType(OrdersActions.placeOrderSuccess),
      tap(({ order }) => {
        router.navigateByUrl('/order/success');
        toast.show('Order ' + order.id + ' raised successfully');
      }),
    ),
  { functional: true, dispatch: false },
);

/** Placing an order changes stock and credit — pull credit, catalog & mystock fresh. */
export const refreshAfterPlaceOrder$ = createEffect(
  (actions$ = inject(Actions)) =>
    actions$.pipe(
      ofType(OrdersActions.placeOrderSuccess),
      switchMap(() =>
        of(
          ProfileActions.loadCreditInfo(),
          CatalogActions.loadCategoryInfo(),
          StockActions.loadStock(),
        ),
      ),
    ),
  { functional: true },
);
